import { useMixpanel } from '@/context/mixpanel'
import type { BorrowMarketRow } from '@/hooks/useBorrowMarkets'
import { useBorrowMarketsSearchState } from '@/hooks/useBorrowMarketsSearchState'
import { cn } from '@/utils/cn'
import { Accordion, AccordionItem } from '../common/accordion'
import { Button } from '../common/button'
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogContentWrapper,
  DialogFooter,
  DialogHeader,
  DialogHeaderWrapper,
  DialogScrollArea,
  DialogTitle,
  DialogTrigger,
} from '../common/dialog'
import { Separator } from '../common/separator'
import { FilterIcon } from '../icons/filter'
import { PlusIcon } from '../icons/plus'
import { FilterAccordionContent, FilterAccordionTrigger } from '../shared/filterSelect'
import { FilterListContent } from '../shared/filterSelectContent'
import { GroupedFilterListContent } from '../shared/groupedFilterSelectContent'
import { BorrowLiquidityFilterAccordion } from './borrowLiquidityFilter'
import { BorrowMarketSizeFilterAccordion } from './borrowMarketSizeFilter'
import { BorrowMarketsSearchInput } from './borrowMarketsSearchInput'
import { BorrowMarketsSortSelect } from './borrowMarketsSortSelect'
import { useBorrowMarketsFilterData } from './useBorrowMarketsFilterData'

interface BorrowMarketsFiltersModalProps {
  borrowMarkets: BorrowMarketRow[]
  numberOfBorrowOptions: number
  className?: string
}

export const BorrowMarketsFiltersModal = ({
  borrowMarkets,
  numberOfBorrowOptions,
  className,
}: BorrowMarketsFiltersModalProps) => {
  const { touchedParamsCount, clearSearchState } = useBorrowMarketsSearchState()
  const { trackEvent } = useMixpanel()

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          variant="default"
          size="default"
          className={cn('relative rounded-xl bg-page ring-inset', className)}
          onClick={() => trackEvent('Mobile filters used', { Page: 'Borrow' })}
        >
          <FilterIcon size={24} />
          {touchedParamsCount > 0 && (
            <p className="pointer-events-none absolute top-1 right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-center font-bold text-page leading-none ring-2 ring-page [font-size:10px]">
              <span className="sr-only">Number of filters applied</span>
              <span>{touchedParamsCount}</span>
            </p>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent position="right">
        <DialogBody className="bg-page">
          <DialogHeaderWrapper className="px-5 md:pt-8">
            <DialogHeader>
              <DialogTitle>Filters</DialogTitle>
            </DialogHeader>
            <Separator />
          </DialogHeaderWrapper>
          <DialogScrollArea>
            <DialogContentWrapper className="px-5 pt-4">
              <div className="flex flex-wrap gap-2">
                <BorrowMarketsSearchInput
                  numberOfBorrowOptions={numberOfBorrowOptions}
                  className="w-fit flex-1 basis-48"
                  trackingParams={{ Page: 'Borrow', Device: 'Mobile' }}
                />
                <BorrowMarketsSortSelect className="flex-1 justify-start" modal />
              </div>
              <BorrowMarketsFilterAccordions borrowMarkets={borrowMarkets} />
              {touchedParamsCount > 0 && (
                <Button variant="input" size="md" onClick={clearSearchState} className="gap-2">
                  <PlusIcon size={20} className="rotate-45" />
                  Clear filters
                </Button>
              )}
            </DialogContentWrapper>
          </DialogScrollArea>
          <DialogFooter className="px-5">
            <DialogClose asChild>
              <Button variant="primary" size="md" className="w-full">
                Confirm
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}

const BorrowMarketsFilterAccordions = ({ borrowMarkets }: Pick<BorrowMarketsFiltersModalProps, 'borrowMarkets'>) => {
  const { tokenFilterProps, collateralFilterProps, networkFilterProps, protocolFilterProps } =
    useBorrowMarketsFilterData(borrowMarkets)

  return (
    <Accordion type="multiple">
      <AccordionItem value="tokens">
        <FilterAccordionTrigger name="tokens" />
        <FilterAccordionContent>
          <GroupedFilterListContent {...tokenFilterProps} scrollClassName="max-h-48" />
        </FilterAccordionContent>
      </AccordionItem>
      <AccordionItem value="network">
        <FilterAccordionTrigger name="network" />
        <FilterAccordionContent>
          <FilterListContent {...networkFilterProps} scrollClassName="max-h-48" />
        </FilterAccordionContent>
      </AccordionItem>
      <AccordionItem value="protocol">
        <FilterAccordionTrigger name="protocol" />
        <FilterAccordionContent>
          <FilterListContent {...protocolFilterProps} scrollClassName="max-h-48" />
        </FilterAccordionContent>
      </AccordionItem>
      <AccordionItem value="collateral">
        <FilterAccordionTrigger name="collateral" />
        <FilterAccordionContent>
          <GroupedFilterListContent {...collateralFilterProps} scrollClassName="max-h-48" />
        </FilterAccordionContent>
      </AccordionItem>
      <BorrowMarketSizeFilterAccordion borrowMarkets={borrowMarkets} />
      <BorrowLiquidityFilterAccordion borrowMarkets={borrowMarkets} />
    </Accordion>
  )
}
